import React, { useEffect, useState, useCallback, useRef } from 'react';
import { View, Image, TextInput, TouchableOpacity, Pressable, Modal } from 'react-native';


import { translate } from '../../services/translate';
import MyAppText from '../../components/MyAppText';
import { Slider } from '@miblanchard/react-native-slider';
import { faArrowUpFromBracket, faCircleInfo, faUser, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import Tooltip from '../../components/Tooltip';
import { MoreThanOneFaceOnPhoto, NoFaceOnPhoto } from '../../services/central-api/facial';
import { smartSearchFacialService } from '../../services/central-api/smart-search-facial';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { Hoverable } from 'react-native-web-hover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import useDebounce from '../../components/hooks/useDebounce';
import SmartSearchEventList from './SmartSearchEventList';
import ThresholdDetailTooltip from './ThresholdDetailTooltip';
import { WindowInformation } from '../../services/window-information';
import Container from '../../components/Container';
import Card from '../../components/Card';
import DataTableHeader from '../../components/DataTableHeader';
import TableTooltip from './TableTooltip';
import DataTableContainer from '../../components/DataTableContainer';


const searchResultsColumns: string[] = [
    'tableColumnRegisteredCapture',
    'tableColumnRegisteredReference',
    'tableColumnCaptureAgainstFacialSent',
    'tableColumnIdentification',
    'tableColumnInformation',
];

export default function SmartSearchFacial() {
    const { styles, theme } = useStyles(stylesheet);
    const windowInfo = WindowInformation();

    const [, updateState] = useState({});
    const forceUpdate = useCallback(() => updateState({}), []);

    const [file, setFile] = useState<{ file: File; objectURI: string; }>();
    const [thresholdSlider, setThresholdSlider] = useState(834);
    const [threshold, setThreshold] = useState(thresholdSlider / 1000);
    const [slidingTimeout, setSlidingTimeout] = useState<NodeJS.Timeout>();

    const [isLoading, setIsLoading] = useState(false);
    const [detections, setDetections] = useState<FacialDetection[]>([]);

    const [inputValue, setInputValue] = useState<string>((834 / 1000).toFixed(3));
    const debouncedInputValue: string = useDebounce<string>(inputValue, 500); // Debounce by 500ms
    const [openThresholdDetailTooltip, setOpenThresholdDetailTooltip] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    async function getFacialDetections(file: File) {
        setIsLoading(true);
        setDetections([]);

        const maxRetries = 5;
        let attempt = 0;
        const delay = 1000;

        while (attempt < maxRetries) {
            try {
                const response = await smartSearchFacialService.findDetectionByPicture({ photo: file, threshold });
                setDetections(response.detections);
                forceUpdate();
                setIsLoading(false);
                return;
            } catch (err) {
                if (err instanceof NoFaceOnPhoto || err instanceof MoreThanOneFaceOnPhoto) {
                    setIsLoading(false);
                    return Toast.show({
                        type: 'sentinelxWarning',
                        text1: translate(err.message),
                    });
                }

                attempt++;
                await new Promise((res) => setTimeout(res, delay));
            }
        }
        Toast.show({
            type: 'sentinelxError',
            text1: translate('limitAttempts'),
        });
        setIsLoading(false);
    }

    useEffect(() => {
        // debounce
        setInputValue((thresholdSlider / 1000).toFixed(3));
        if (slidingTimeout) {
            clearTimeout(slidingTimeout);
        }

        const timeout = setTimeout(() => {
            setThreshold(thresholdSlider / 1000);
        }, 1000);

        setSlidingTimeout(timeout);

    }, [thresholdSlider]);

    useEffect(() => {
        if (file) {
            getFacialDetections(file.file);
        }
    }, [threshold, file]);

    useEffect(() => {

        const handleDebouncedChange = () => {
            const cleanedVal: string = debouncedInputValue.replace(/[^0-9.]/g, '');

            const parts: string[] = cleanedVal.split('.');
            const numericText: string = parts.shift() + (parts.length > 0 ? '.' + parts.join('') : '');

            let numericValue: number = parseFloat(numericText);

            if (isNaN(numericValue) || numericValue < 0) {
                numericValue = 0;
            } else if (numericValue > 1) {
                numericValue = 1;
            }

            if (numericText === '') {
                setThresholdSlider(0);
            } else {
                setThresholdSlider(Math.round(Math.max(0, Math.min(1000, numericValue * 1000))));
            }
        };

        if (debouncedInputValue !== null && debouncedInputValue !== undefined) {
            handleDebouncedChange();
        }

    }, [debouncedInputValue]);

    function handleImmediateChange(val: string) {
        let filteredVal = val.replace(/[^0-9.]/g, '');

        const parts = filteredVal.split('.');
        if (parts.length > 2) {
            filteredVal = parts[0] + '.' + parts.slice(1).join('');
        }

        if (filteredVal.startsWith('.') && filteredVal.length > 1) {
            filteredVal = '0' + filteredVal;
        }

        if (filteredVal.length > 1 && filteredVal.startsWith('0') && filteredVal[1] !== '.') {
            filteredVal = filteredVal.substring(1);
        }

        if (filteredVal === '00') {
            filteredVal = '0';
        }

        setInputValue(filteredVal);
    }

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return <Container style={{
        rowGap: 15,
        padding: 14
    }}>
        <View style={[styles.header, windowInfo.isMobile ? { flexDirection: 'column' } : { flexDirection: 'row' }]}>
            <Hoverable style={[windowInfo.isMobile ? { alignItems: 'center' } : {}]}>
                {({ hovered }) => (<>
                    <View style={{ position: 'relative' }}>
                        <Pressable onPress={handleUploadClick}>
                            {file
                                ? <Image
                                    style={styles.imageContainer}
                                    source={{
                                        uri: file.objectURI
                                    }}
                                />
                                : <View style={[styles.imageContainer, {
                                    backgroundColor: theme.colors.neutral['200'],
                                    justifyContent: 'center',
                                    alignContent: 'center'
                                },]}>
                                    <FontAwesomeIcon color={theme.colors.neutral['300']} icon={faUser} fontSize={32} />
                                </View>}
                        </Pressable>
                        {hovered && <TouchableOpacity
                            style={[styles.imageContainer, {
                                position: 'absolute',
                                top: 0,
                                backgroundColor: '#000',
                                opacity: file ? 0.7 : 0.5,
                                justifyContent: 'center',
                                alignContent: 'center'
                            }]}
                            onPress={handleUploadClick}
                        >
                            <View style={{ gap: 6 }}>
                                <FontAwesomeIcon color={theme.colors.white['500']} icon={file ? faPen as IconDefinition : faArrowUpFromBracket} fontSize={16} />
                                <MyAppText
                                    style={{
                                        color: theme.colors.white['500'],
                                        fontWeight: '600',
                                        fontSize: 12,
                                        textAlign: 'center',
                                    }}
                                >{translate(file ? 'changeImage' : 'sendImage')}</MyAppText>
                            </View>
                        </TouchableOpacity>}
                    </View>
                    <input
                        type='file'
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            if (!e.target.files || !e.target.files.length) {
                                return;
                            }
                            const file = e.target.files[0];
                            setFile({ file, objectURI: URL.createObjectURL(file) });
                        }}
                        ref={(input) => (fileInputRef.current = input)}
                        accept='image/*'
                    />
                </>)}

            </Hoverable>
            <View style={{ flexDirection: 'column', gap: 10 }}>
                <MyAppText style={styles.title}>
                    {translate('facialSending')}
                </MyAppText>
                <View style={{ flexDirection: 'column', gap: 4 }}>
                    {windowInfo.isMobile
                        ? <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                            <MyAppText style={styles.confidenceLimit}>{translate('confidenceLimit')}</MyAppText>
                            <TouchableOpacity onPress={() => setOpenThresholdDetailTooltip(true)}>
                                <FontAwesomeIcon icon={faCircleInfo} fontSize={12} color={theme.colors.neutral[500]} />
                            </TouchableOpacity>
                        </View>
                        : <Tooltip useSimpleBox tooltipDirection='right' tooltipContent={<ThresholdDetailTooltip />} style={{ width: 300 }}>
                            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                                <MyAppText style={styles.confidenceLimit}>{translate('confidenceLimit')}</MyAppText>
                                <FontAwesomeIcon icon={faCircleInfo} fontSize={12} color={theme.colors.neutral[500]} />
                            </View>
                        </Tooltip>}
                    <View style={styles.sliderContainer}>
                        <View style={windowInfo.isMobile ? styles.rowSliderMobile : styles.rowSlider}>
                            <Slider
                                animateTransitions
                                minimumValue={0}
                                maximumValue={1000}
                                minimumTrackTintColor={theme.colors.neutral['400']}
                                thumbTintColor={theme.colors.neutral['400']}
                                maximumTrackTintColor={theme.colors.neutral['300']}
                                value={thresholdSlider}
                                step={1}
                                onValueChange={(value) => setThresholdSlider(value[0])}
                            />
                        </View>
                        <TextInput
                            keyboardType="numeric"
                            maxLength={5}
                            style={styles.thresholdInput}
                            value={inputValue}
                            onChangeText={handleImmediateChange}
                        />
                    </View>
                </View>
            </View>
        </View>
        <Card style={{ zIndex: 2 }}>
            <DataTableHeader
                title={translate('searchResults')}
                titleTooltip={<TableTooltip
                    title='searchResults'
                    subTitle='smartSearchTableTooltipSubTitle'
                    bodyTitle='aboutTheTableColumns'
                    bodyItems={searchResultsColumns}
                />}
                zIndex={2}
                filter={<MyAppText style={[styles.totalResults, windowInfo.isMobile && { alignSelf: 'flex-end' }]}>{translate('totalResults')}: {detections.length}</MyAppText>}
            />
            <DataTableContainer
                dataTable={<SmartSearchEventList
                    events={detections}
                    facialSent={file?.objectURI ?? ''}
                    isLoading={isLoading}
                    noResultsText={!file ? '' : undefined}
                />}
            />
        </Card>
        <Modal
            visible={openThresholdDetailTooltip}
            animationType='fade'
            onRequestClose={() => {
                setOpenThresholdDetailTooltip(false);
            }}
            transparent={true}
            collapsable
            style={{
                zIndex: 3
            }}
        >
            <Pressable style={styles.modalContainer} onPressOut={() => setOpenThresholdDetailTooltip(false)}>
                <View style={{ width: '95%' }}>
                    <ThresholdDetailTooltip />
                </View>
            </Pressable>
        </Modal>
    </Container>;
}

const stylesheet = createStyleSheet(theme => ({
    modalContainer: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    rowSlider: {
        width: 150,
        marginRight: 16
    },
    rowSliderMobile: {
        width: '75%',
        maxWidth: 300,
        marginRight: 16
    },
    header: {
        display: 'flex',
        gap: 16,
        flexWrap: 'wrap',
        zIndex: 3,
    },
    totalResults: {
        fontWeight: '500',
        fontSize: 14,
        color: theme.colors.neutral['400']
    },
    imageContainer: {
        height: 96,
        width: 72,
        borderRadius: 4,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.field.border
    },
    sliderContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 31,
    },
    thresholdInput: {
        fontFamily: 'Open Sans',
        backgroundColor: theme.colors.field.background,
        borderWidth: 1,
        borderRadius: 5,
        borderColor: theme.colors.neutral['300'],
        fontSize: 14,
        color: theme.colors.neutral['400'],
        width: 54,
        paddingHorizontal: 8,
        paddingVertical: 6
    },
    title: {
        fontWeight: '700',
        fontSize: 14,
        color: theme.colors.primary['500']
    },
    confidenceLimit: {
        fontWeight: '600',
        fontSize: 14,
        color: theme.colors.neutral['500']
    }
}));
